/**
 * Saved-water-profile commands — the workspace's water library (PLAN §4.3,
 * "standalone under Water for profile management").
 *
 * Structurally identical to the equipment-profile four: create (optionally from
 * a template), update, delete, restore. Built-ins are read-only, and the escape
 * hatch is `waterProfile.create({ from: builtinProfile })` — duplicate, then
 * edit — which keeps the shipped city analyses byte-identical on every device
 * and in every replay, because they are part of the deterministic seed state.
 *
 * Deleting a profile does **not** touch any recipe. A recipe's water section
 * holds a *copy* of the six ions, never a pointer at a library id (see
 * `document/water-profile.ts` on why), so there is no dangling reference to
 * resolve and delete stays a single-document, exactly-invertible change.
 */

import type { SavedWaterProfileDoc } from '../../document/water-profile';
import { BUILTIN_WATER_PROFILES, RO_WATER_PROFILE_ID } from '../../document/water-profile';
import type { WorkspaceState } from '../../document/workspace';
import { defineCommand, inverseCommand } from '../definition';
import { commandError } from '../envelope';
import { quoted } from '../format';
import { mergeDefined, priorValues } from '../helpers';
import * as p from '../payloads';

/**
 * The profile a new water document starts from when the caller supplies none.
 *
 * RO — all zeros. A blank water analysis is genuinely empty, and defaulting to
 * a mineralised profile would silently attribute someone else's ions to the
 * brewer's tap.
 */
const WATER_TEMPLATE = BUILTIN_WATER_PROFILES.find((profile) => profile.id === RO_WATER_PROFILE_ID);

/** A copy of `state` with one water profile replaced or added. */
function putProfile(state: WorkspaceState, profile: SavedWaterProfileDoc): WorkspaceState {
  return { ...state, waterProfiles: { ...state.waterProfiles, [profile.id]: profile } };
}

/** Create a saved water profile, optionally by duplicating an existing one. */
export const waterProfileCreate = defineCommand({
  type: 'waterProfile.create',
  payload: p.waterProfileCreatePayloadSchema,
  apply: (state, command) => {
    const { profileId, name, from } = command.payload;
    if (state.waterProfiles[profileId] !== undefined) {
      return {
        ok: false,
        error: commandError('conflict', `A water profile with id "${profileId}" already exists.`, {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    const template = from ?? WATER_TEMPLATE;
    if (template === undefined) {
      return {
        ok: false,
        error: commandError('invalid-state', 'No water profile template available.', {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    const profile: SavedWaterProfileDoc = {
      ...template,
      id: profileId,
      name,
      builtin: false,
      createdAt: command.timestamp,
      updatedAt: command.timestamp,
    };
    return { ok: true, state: putProfile(state, profile) };
  },
  invert: (_state, command) =>
    inverseCommand(
      command,
      'waterProfile.delete',
      { profileId: command.payload.profileId },
      command.timestamp,
    ),
  summarize: (_state, command) => `Create water profile ${quoted(command.payload.name)}`,
});

/** Change fields of a saved water profile. Built-ins refuse. */
export const waterProfileUpdate = defineCommand({
  type: 'waterProfile.update',
  payload: p.waterProfileUpdatePayloadSchema,
  apply: (state, command) => {
    const profile = state.waterProfiles[command.payload.profileId];
    if (profile === undefined) {
      return {
        ok: false,
        error: commandError(
          'not-found',
          `No water profile with id "${command.payload.profileId}".`,
          { commandId: command.commandId, commandType: command.type },
        ),
      };
    }
    if (profile.builtin) {
      return {
        ok: false,
        error: commandError(
          'invalid-state',
          `${quoted(profile.name)} is a built-in profile. Duplicate it before editing.`,
          { commandId: command.commandId, commandType: command.type },
        ),
      };
    }
    return {
      ok: true,
      state: putProfile(state, {
        ...mergeDefined(profile, command.payload.changes),
        updatedAt: command.timestamp,
      }),
    };
  },
  invert: (state, command) => {
    const profile = state.waterProfiles[command.payload.profileId];
    return profile === undefined
      ? null
      : inverseCommand(
          command,
          'waterProfile.update',
          {
            profileId: profile.id,
            changes: priorValues(profile, command.payload.changes),
          },
          profile.updatedAt,
        );
  },
  summarize: (state, command) => {
    const profile = state.waterProfiles[command.payload.profileId];
    const keys = Object.keys(command.payload.changes).sort().join(', ');
    return `Update water profile ${quoted(profile?.name ?? command.payload.profileId)}${
      keys === '' ? '' : ` (${keys})`
    }`;
  },
});

/** Delete a saved water profile. Built-ins refuse; recipes are untouched. */
export const waterProfileDelete = defineCommand({
  type: 'waterProfile.delete',
  payload: p.waterProfileDeletePayloadSchema,
  apply: (state, command) => {
    const profile = state.waterProfiles[command.payload.profileId];
    if (profile === undefined) {
      return {
        ok: false,
        error: commandError(
          'not-found',
          `No water profile with id "${command.payload.profileId}".`,
          { commandId: command.commandId, commandType: command.type },
        ),
      };
    }
    if (profile.builtin) {
      return {
        ok: false,
        error: commandError(
          'invalid-state',
          `${quoted(profile.name)} is a built-in profile and cannot be deleted.`,
          { commandId: command.commandId, commandType: command.type },
        ),
      };
    }
    const waterProfiles = { ...state.waterProfiles };
    delete waterProfiles[command.payload.profileId];
    return { ok: true, state: { ...state, waterProfiles } };
  },
  invert: (state, command) => {
    const profile = state.waterProfiles[command.payload.profileId];
    return profile === undefined
      ? null
      : inverseCommand(command, 'waterProfile.restore', { profile }, profile.updatedAt);
  },
  summarize: (state, command) => {
    const profile = state.waterProfiles[command.payload.profileId];
    return `Delete water profile ${quoted(profile?.name ?? command.payload.profileId)}`;
  },
});

/** Insert a saved water profile verbatim — an upsert, and the inverse of `waterProfile.delete`. */
export const waterProfileRestore = defineCommand({
  type: 'waterProfile.restore',
  payload: p.waterProfileRestorePayloadSchema,
  apply: (state, command) => ({ ok: true, state: putProfile(state, command.payload.profile) }),
  invert: (state, command) => {
    const prior = state.waterProfiles[command.payload.profile.id];
    return prior === undefined
      ? inverseCommand(
          command,
          'waterProfile.delete',
          { profileId: command.payload.profile.id },
          command.timestamp,
        )
      : inverseCommand(command, 'waterProfile.restore', { profile: prior }, prior.updatedAt);
  },
  summarize: (_state, command) => `Restore water profile ${quoted(command.payload.profile.name)}`,
});
